export type SystemType = 'content' | 'webapp' | 'traffic' | 'app'

export interface ComponentData {
  id: string
  label: string
  description: string
  technologies: string[]
  tradeoff: string
  x: number
  y: number
  width: number
  height: number
  isPrimary?: boolean
  // Enhanced fields for interactivity
  responsibility: string
  failureMode: string
  scalingConstraint: string
  connectedTo: string[]
}

export interface FailureScenario {
  name: string
  description: string
  affectedComponents: string[]
  impact: string
  mitigation: string
}

export interface SystemData {
  title: string
  components: ComponentData[]
  narrative: {
    optimizes: string
    breaksDown: string
    takeaway: string
  }
  failureScenarios: FailureScenario[]
}

export const systemData: Record<SystemType, SystemData> = {
  content: {
    title: 'Content Platform',
    components: [
      {
        id: 'client',
        label: 'User',
        description: 'Readers and visitors consuming content.',
        technologies: [],
        tradeoff: 'Performance expectations vs. content freshness.',
        x: 50,
        y: 5,
        width: 18,
        height: 7,
        isPrimary: true,
        responsibility: 'Requests content and expects fast page loads globally.',
        failureMode: 'Experiences slow load times or stale content when CDN/cache fails.',
        scalingConstraint: 'Limited by network latency and browser capabilities.',
        connectedTo: ['cdn']
      },
      {
        id: 'cdn',
        label: 'CDN',
        description: 'Serves static assets and cached content globally.',
        technologies: ['Cloudflare', 'Fastly', 'Vercel Edge'],
        tradeoff: 'Cost vs. global performance.',
        x: 50,
        y: 20,
        width: 28,
        height: 9,
        isPrimary: true,
        responsibility: 'Distributes cached content globally, shields origin from direct traffic.',
        failureMode: 'Cache misses force all requests to origin, overwhelming backend.',
        scalingConstraint: 'Edge location coverage and cache invalidation latency.',
        connectedTo: ['client', 'frontend']
      },
      {
        id: 'frontend',
        label: 'Frontend',
        description: 'Renders pages and handles client-side interactions.',
        technologies: ['Next.js', 'Nuxt', 'Astro'],
        tradeoff: 'Static generation speed vs. dynamic flexibility.',
        x: 20,
        y: 38,
        width: 22,
        height: 10,
        isPrimary: true,
        responsibility: 'Pre-renders content, serves static HTML, handles incremental regeneration.',
        failureMode: 'Build failures prevent new content from being published.',
        scalingConstraint: 'Build time grows with content volume.',
        connectedTo: ['cdn', 'api', 'cache']
      },
      {
        id: 'cms',
        label: 'CMS',
        description: 'Editorial interface for content creation and management.',
        technologies: ['Strapi', 'Contentful', 'Sanity'],
        tradeoff: 'Editorial freedom vs. structured workflows.',
        x: 78,
        y: 38,
        width: 22,
        height: 10,
        isPrimary: false,
        responsibility: 'Provides content authoring interface, triggers rebuild webhooks.',
        failureMode: 'Editors lose access to content management, updates halt.',
        scalingConstraint: 'Editor count and content relationship complexity.',
        connectedTo: ['api']
      },
      {
        id: 'cache',
        label: 'Cache',
        description: 'Stores pre-rendered pages and API responses.',
        technologies: ['Redis', 'Varnish', 'In-memory'],
        tradeoff: 'Memory cost vs. response time.',
        x: 15,
        y: 60,
        width: 22,
        height: 10,
        isPrimary: false,
        responsibility: 'Reduces database load by caching frequently accessed data.',
        failureMode: 'Cache failure causes 10-100x increase in database queries.',
        scalingConstraint: 'Memory capacity and invalidation complexity.',
        connectedTo: ['api']
      },
      {
        id: 'api',
        label: 'API',
        description: 'Fetches and serves content data.',
        technologies: ['Node.js', 'Laravel', 'Python'],
        tradeoff: 'Simplicity vs. feature richness.',
        x: 48,
        y: 60,
        width: 22,
        height: 10,
        isPrimary: true,
        responsibility: 'Orchestrates content retrieval, applies business logic, manages webhooks.',
        failureMode: 'API downtime blocks all content updates and dynamic requests.',
        scalingConstraint: 'CPU/memory per request and database connection pooling.',
        connectedTo: ['frontend', 'cms', 'database', 'storage']
      },
      {
        id: 'database',
        label: 'Database',
        description: 'Stores content, metadata, and relationships.',
        technologies: ['PostgreSQL', 'MySQL', 'MongoDB'],
        tradeoff: 'Query flexibility vs. write performance.',
        x: 78,
        y: 60,
        width: 22,
        height: 10,
        isPrimary: true,
        responsibility: 'Maintains content relationships, ensures data consistency.',
        failureMode: 'Without aggressive caching, read-heavy platforms collapse under write spikes.',
        scalingConstraint: 'Write throughput and complex query performance.',
        connectedTo: ['api']
      },
      {
        id: 'storage',
        label: 'Object Storage',
        description: 'Stores media files, static assets, and large objects.',
        technologies: ['S3', 'R2', 'Cloud Storage'],
        tradeoff: 'Cost vs. access speed and durability.',
        x: 48,
        y: 80,
        width: 28,
        height: 9,
        isPrimary: false,
        responsibility: 'Stores and serves media assets, handles large file uploads.',
        failureMode: 'Media becomes unavailable, breaking images and downloads.',
        scalingConstraint: 'Bandwidth and request rate limits.',
        connectedTo: ['api']
      }
    ],
    narrative: {
      optimizes: 'Content platforms optimize for read performance and editorial workflows. They handle predictable, read-heavy traffic where content changes infrequently compared to views.',
      breaksDown: 'This architecture breaks down when content structure changes frequently, when real-time updates are required, or when write operations become as common as reads.',
      takeaway: 'Separate content creation from delivery, and cache aggressively.'
    },
    failureScenarios: [
      {
        name: 'Cache Failure',
        description: 'Cache layer becomes unavailable, overwhelming the database',
        affectedComponents: ['cache', 'database', 'api'],
        impact: 'Without caching, all read requests hit the database directly, causing 10-100x load increase. Response times spike from <50ms to >2000ms. Database connections saturate.',
        mitigation: 'Implement multi-layer caching (Redis + in-memory), use read replicas, add rate limiting at API level, enable stale-while-revalidate patterns.'
      },
      {
        name: 'CDN Outage',
        description: 'CDN fails or cache invalidation breaks, forcing all traffic to origin',
        affectedComponents: ['cdn', 'frontend', 'api'],
        impact: 'Origin servers receive 50-100x normal traffic. Bandwidth costs spike. Global latency degrades as users hit distant origin servers instead of nearby edge locations.',
        mitigation: 'Multi-CDN strategy, origin shield layer, automatic failover, graceful degradation with cached versions.'
      },
      {
        name: 'Build System Failure',
        description: 'Static site generation fails, preventing content updates',
        affectedComponents: ['frontend', 'cms', 'api'],
        impact: 'New content cannot be published. Editorial team blocked. Hot fixes cannot be deployed. Users see stale content with no update path.',
        mitigation: 'Incremental static regeneration (ISR), on-demand revalidation, fallback to SSR for critical pages, build queue with retry logic.'
      }
    ]
  },
  webapp: {
    title: 'Web Application',
    components: [
      {
        id: 'user',
        label: 'User',
        description: 'Authenticated users interacting with the application.',
        technologies: [],
        tradeoff: 'User experience vs. security complexity.',
        x: 50,
        y: 5,
        width: 18,
        height: 7,
        isPrimary: true,
        responsibility: 'Performs authenticated actions requiring authorization.',
        failureMode: 'Loses access to application features when auth fails.',
        scalingConstraint: 'Session storage and concurrent user capacity.',
        connectedTo: ['frontend']
      },
      {
        id: 'frontend',
        label: 'Frontend',
        description: 'Application interface with authentication and state management.',
        technologies: ['React', 'Vue', 'Svelte'],
        tradeoff: 'Client-side complexity vs. server round-trips.',
        x: 50,
        y: 22,
        width: 28,
        height: 12,
        isPrimary: true,
        responsibility: 'Manages application state, validates user input, handles navigation.',
        failureMode: 'Client errors break user workflows, state corruption requires refresh.',
        scalingConstraint: 'Bundle size and client-side memory.',
        connectedTo: ['user', 'api']
      },
      {
        id: 'api',
        label: 'API',
        description: 'Handles business logic, authentication, and data orchestration.',
        technologies: ['Laravel', 'Node.js', 'NestJS'],
        tradeoff: 'Flexibility vs. stricter domain modeling.',
        x: 50,
        y: 45,
        width: 28,
        height: 12,
        isPrimary: true,
        responsibility: 'Enforces business rules, validates permissions, orchestrates data flow.',
        failureMode: 'API downtime blocks all user actions and data access.',
        scalingConstraint: 'Request throughput and third-party API dependencies.',
        connectedTo: ['frontend', 'auth', 'database', 'jobs']
      },
      {
        id: 'auth',
        label: 'Auth',
        description: 'Manages user sessions, permissions, and access control.',
        technologies: ['JWT', 'OAuth', 'Session-based'],
        tradeoff: 'Stateless scaling vs. immediate revocation.',
        x: 15,
        y: 68,
        width: 22,
        height: 10,
        isPrimary: false,
        responsibility: 'Issues tokens, validates sessions, manages permission scopes.',
        failureMode: 'Auth failure cascades to complete access denial across the system.',
        scalingConstraint: 'Token validation overhead and session storage capacity.',
        connectedTo: ['api']
      },
      {
        id: 'database',
        label: 'Database',
        description: 'Stores application data with relationships and constraints.',
        technologies: ['PostgreSQL', 'MySQL'],
        tradeoff: 'Data integrity vs. query performance.',
        x: 48,
        y: 68,
        width: 22,
        height: 10,
        isPrimary: true,
        responsibility: 'Maintains ACID guarantees, enforces referential integrity.',
        failureMode: 'Data corruption or downtime halts all operations.',
        scalingConstraint: 'Transaction throughput and connection pool limits.',
        connectedTo: ['api']
      },
      {
        id: 'jobs',
        label: 'Background Jobs',
        description: 'Processes async tasks like emails, reports, and data processing.',
        technologies: ['Bull', 'Sidekiq', 'Celery'],
        tradeoff: 'Job reliability vs. processing speed.',
        x: 78,
        y: 68,
        width: 22,
        height: 10,
        isPrimary: false,
        responsibility: 'Handles asynchronous work without blocking user requests.',
        failureMode: 'Queue backup causes delayed notifications and processing.',
        scalingConstraint: 'Worker count and memory per job.',
        connectedTo: ['api']
      }
    ],
    narrative: {
      optimizes: 'Web applications optimize for business logic, data integrity, and user workflows. They handle authenticated interactions where data consistency and authorization matter more than raw read performance.',
      breaksDown: 'This architecture breaks down when read traffic vastly exceeds writes, when real-time collaboration is required, or when the API becomes a bottleneck for high-frequency operations.',
      takeaway: 'Keep the API boundary clear, and process asynchronously what doesn\'t need immediate response.'
    },
    failureScenarios: [
      {
        name: 'Auth Service Failure',
        description: 'Authentication service becomes unavailable, blocking all user access',
        affectedComponents: ['auth', 'api', 'frontend'],
        impact: 'All authenticated requests fail. Users cannot login, existing sessions cannot be validated. API rejects requests. Complete service unavailability despite healthy infrastructure.',
        mitigation: 'Implement auth service redundancy, use cached session validation with grace periods, add circuit breakers, allow degraded mode for read-only access.'
      },
      {
        name: 'Database Connection Pool Exhaustion',
        description: 'All database connections consumed, blocking new operations',
        affectedComponents: ['database', 'api'],
        impact: 'New requests timeout waiting for database connections. Application grinds to halt despite low actual database load. Users see timeout errors or hung requests.',
        mitigation: 'Implement connection pooling with limits, add request queuing, use read replicas to distribute load, implement graceful backpressure and rejection.'
      },
      {
        name: 'Background Job Queue Backup',
        description: 'Job queue fills faster than workers process, causing delays',
        affectedComponents: ['jobs', 'api'],
        impact: 'Emails delayed by hours. Reports never generate. Data processing lags. Users experience missing functionality. Memory grows until system crashes.',
        mitigation: 'Add job prioritization, implement dead letter queues, auto-scale workers, add queue depth alerts, separate critical from non-critical queues.'
      }
    ]
  },
  traffic: {
    title: 'High-Traffic System',
    components: [
      {
        id: 'users',
        label: 'Users',
        description: 'High volume of concurrent requests.',
        technologies: [],
        tradeoff: 'Response time vs. system capacity.',
        x: 50,
        y: 5,
        width: 18,
        height: 7,
        isPrimary: true,
        responsibility: 'Generates unpredictable load spikes requiring elastic scaling.',
        failureMode: 'Experiences timeouts and errors during traffic surges.',
        scalingConstraint: 'Network bandwidth and geographic distribution.',
        connectedTo: ['loadbalancer']
      },
      {
        id: 'loadbalancer',
        label: 'Load Balancer',
        description: 'Distributes traffic across multiple servers.',
        technologies: ['Nginx', 'HAProxy', 'Cloud Load Balancer'],
        tradeoff: 'Distribution strategy vs. session affinity.',
        x: 50,
        y: 22,
        width: 28,
        height: 9,
        isPrimary: true,
        responsibility: 'Routes requests evenly, performs health checks, handles SSL termination.',
        failureMode: 'Single point of failure causes complete service outage.',
        scalingConstraint: 'Requests per second and health check overhead.',
        connectedTo: ['users', 'app1', 'app2', 'app3']
      },
      {
        id: 'app1',
        label: 'App Servers',
        description: 'Multiple instances handling requests in parallel.',
        technologies: ['Node.js', 'Go', 'Python'],
        tradeoff: 'Instance count vs. resource efficiency.',
        x: 20,
        y: 42,
        width: 18,
        height: 10,
        isPrimary: true,
        responsibility: 'Processes requests stateless-ly, allows horizontal scaling.',
        failureMode: 'Instance failure reduces capacity, triggering cascading overload.',
        scalingConstraint: 'Memory per instance and startup time.',
        connectedTo: ['loadbalancer', 'cache', 'database']
      },
      {
        id: 'app2',
        label: 'App Servers',
        description: 'Multiple instances handling requests in parallel.',
        technologies: ['Node.js', 'Go', 'Python'],
        tradeoff: 'Instance count vs. resource efficiency.',
        x: 50,
        y: 42,
        width: 18,
        height: 10,
        isPrimary: true,
        responsibility: 'Processes requests stateless-ly, allows horizontal scaling.',
        failureMode: 'Instance failure reduces capacity, triggering cascading overload.',
        scalingConstraint: 'Memory per instance and startup time.',
        connectedTo: ['loadbalancer', 'queue', 'database']
      },
      {
        id: 'app3',
        label: 'App Servers',
        description: 'Multiple instances handling requests in parallel.',
        technologies: ['Node.js', 'Go', 'Python'],
        tradeoff: 'Instance count vs. resource efficiency.',
        x: 80,
        y: 42,
        width: 18,
        height: 10,
        isPrimary: true,
        responsibility: 'Processes requests stateless-ly, allows horizontal scaling.',
        failureMode: 'Instance failure reduces capacity, triggering cascading overload.',
        scalingConstraint: 'Memory per instance and startup time.',
        connectedTo: ['loadbalancer', 'database']
      },
      {
        id: 'cache',
        label: 'Cache Layer',
        description: 'Reduces database load with frequently accessed data.',
        technologies: ['Redis', 'Memcached'],
        tradeoff: 'Cache invalidation complexity vs. load reduction.',
        x: 15,
        y: 68,
        width: 22,
        height: 10,
        isPrimary: false,
        responsibility: 'Absorbs read traffic, enables sub-millisecond response times.',
        failureMode: 'Cache failure causes 50-100x database load increase.',
        scalingConstraint: 'Memory capacity and replication lag.',
        connectedTo: ['app1']
      },
      {
        id: 'queue',
        label: 'Queue',
        description: 'Decouples heavy processing from request handling.',
        technologies: ['RabbitMQ', 'SQS', 'Bull'],
        tradeoff: 'Message durability vs. processing latency.',
        x: 48,
        y: 68,
        width: 22,
        height: 10,
        isPrimary: false,
        responsibility: 'Buffers work during spikes, enables async processing.',
        failureMode: 'Queue backup delays critical operations, causes timeouts.',
        scalingConstraint: 'Message throughput and storage capacity.',
        connectedTo: ['app2']
      },
      {
        id: 'database',
        label: 'Database',
        description: 'Primary data store with replication for read scaling.',
        technologies: ['PostgreSQL', 'MySQL'],
        tradeoff: 'Consistency guarantees vs. read performance.',
        x: 78,
        y: 68,
        width: 22,
        height: 10,
        isPrimary: true,
        responsibility: 'Maintains source of truth, handles writes sequentially.',
        failureMode: 'Load balancer failure causes cascading overload across all layers.',
        scalingConstraint: 'Write throughput (cannot horizontally scale writes easily).',
        connectedTo: ['app1', 'app2', 'app3']
      }
    ],
    narrative: {
      optimizes: 'High-traffic systems optimize for horizontal scaling and graceful degradation. They handle unpredictable load patterns where availability and response time matter more than perfect consistency.',
      breaksDown: 'This architecture breaks down when stateful operations require strong consistency, when cache invalidation becomes a bottleneck, or when the queue becomes a single point of failure.',
      takeaway: 'Distribute load, cache strategically, and queue work that can wait.'
    },
    failureScenarios: [
      {
        name: 'Load Balancer Failure',
        description: 'Single load balancer becomes unavailable, creating single point of failure',
        affectedComponents: ['loadbalancer', 'app1', 'app2', 'app3'],
        impact: 'Complete service outage despite healthy app servers. All incoming traffic blocked. No failover path. Revenue loss proportional to downtime.',
        mitigation: 'Deploy load balancers in HA pairs, use DNS failover, implement health checks, consider cloud-native load balancers with automatic failover.'
      },
      {
        name: 'Cache Stampede',
        description: 'Popular cache key expires, causing simultaneous database queries',
        affectedComponents: ['cache', 'database', 'app1', 'app2', 'app3'],
        impact: 'Hundreds of concurrent requests hit database for same data. Database CPU spikes to 100%. Response times degrade from 50ms to 10s+. Cascading failures across all servers.',
        mitigation: 'Implement cache warming, use probabilistic early expiration, add request coalescing, set cache locks, use stale-while-revalidate pattern.'
      },
      {
        name: 'Traffic Spike (10x Normal)',
        description: 'Sudden traffic surge overwhelms system capacity',
        affectedComponents: ['app1', 'app2', 'app3', 'database', 'queue'],
        impact: 'App servers saturate CPU/memory. Database connections exhaust. Queue fills up. New requests timeout or fail. Existing users see degraded performance.',
        mitigation: 'Implement auto-scaling with headroom, add rate limiting, use admission control, shed non-critical load, enable graceful degradation mode.'
      }
    ]
  },
  app: {
    title: 'App-based System',
    components: [
      {
        id: 'mobile',
        label: 'Mobile App',
        description: 'Native or cross-platform application.',
        technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin'],
        tradeoff: 'Platform-specific features vs. code reuse.',
        x: 25,
        y: 10,
        width: 24,
        height: 12,
        isPrimary: true,
        responsibility: 'Provides native platform experience, handles offline scenarios.',
        failureMode: 'API incompatibility breaks app, requires store update to fix.',
        scalingConstraint: 'App store deployment delays and version fragmentation.',
        connectedTo: ['api']
      },
      {
        id: 'web',
        label: 'Web App',
        description: 'Progressive web app or responsive web interface.',
        technologies: ['React', 'Vue', 'Next.js'],
        tradeoff: 'Web reach vs. native capabilities.',
        x: 75,
        y: 10,
        width: 24,
        height: 12,
        isPrimary: true,
        responsibility: 'Provides immediate access without installation, broad compatibility.',
        failureMode: 'Browser API limitations prevent full feature parity.',
        scalingConstraint: 'Browser capabilities and network dependency.',
        connectedTo: ['api']
      },
      {
        id: 'api',
        label: 'API',
        description: 'Shared backend serving multiple client types.',
        technologies: ['REST', 'GraphQL', 'gRPC'],
        tradeoff: 'API flexibility vs. client-specific optimization.',
        x: 50,
        y: 38,
        width: 28,
        height: 12,
        isPrimary: true,
        responsibility: 'Provides unified interface, handles versioning, manages client diversity.',
        failureMode: 'API downtime affects all platforms simultaneously.',
        scalingConstraint: 'Supporting multiple client versions and platform-specific needs.',
        connectedTo: ['mobile', 'web', 'database']
      },
      {
        id: 'database',
        label: 'Database',
        description: 'Centralized data store for all clients.',
        technologies: ['PostgreSQL', 'MongoDB'],
        tradeoff: 'Data model flexibility vs. query performance.',
        x: 50,
        y: 62,
        width: 28,
        height: 10,
        isPrimary: true,
        responsibility: 'Centralizes data for all platforms, handles sync conflicts.',
        failureMode: 'Schema changes require coordinated multi-platform deployments.',
        scalingConstraint: 'Balancing schema flexibility across different client needs.',
        connectedTo: ['api']
      }
    ],
    narrative: {
      optimizes: 'App-based systems optimize for multiple client types from a shared backend. They balance platform-specific needs with shared business logic, requiring careful API design and data synchronization.',
      breaksDown: 'This architecture breaks down when client-specific requirements diverge significantly, when offline-first capabilities are needed, or when the API becomes a bottleneck for platform-specific optimizations.',
      takeaway: 'Design the API for multiple clients from the start, not as an afterthought.'
    },
    failureScenarios: [
      {
        name: 'Breaking API Change',
        description: 'Backend deploys breaking API change without versioning, crashing older clients',
        affectedComponents: ['api', 'mobile', 'web'],
        impact: 'Older app versions crash on launch. Users cannot access service without updating. App store reviews tank. Web users forced to hard refresh. Support tickets flood in.',
        mitigation: 'Implement API versioning from day one, support N-1 versions minimum, use feature flags for gradual rollout, add client version negotiation, monitor client version distribution.'
      },
      {
        name: 'API Rate Limit Exceeded',
        description: 'Aggressive polling or inefficient queries trigger rate limits',
        affectedComponents: ['api', 'mobile', 'web'],
        impact: 'Clients receive 429 errors. Features stop working. Users see "service unavailable" despite healthy backend. Mobile apps drain battery with retry loops.',
        mitigation: 'Implement per-client rate limits, add exponential backoff in clients, use WebSockets for real-time updates, add request batching, monitor client request patterns.'
      },
      {
        name: 'Database Schema Migration Failure',
        description: 'Schema change incompatible with running application code',
        affectedComponents: ['database', 'api'],
        impact: 'API queries fail after migration. Rollback difficult due to data changes. Downtime extends as team debugs. Both mobile and web affected simultaneously.',
        mitigation: 'Use expand-contract pattern (add new columns before removing old), blue-green deployments, backward-compatible migrations, test migrations on production copies.'
      }
    ]
  }
}
